import React, { useState, createContext, useContext, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { Box, CssBaseline, useMediaQuery, useTheme } from '@mui/material';
import Sidebar from './Sidebar';
import TopBar from './TopBar';
import { styled } from '@mui/material/styles';

const DRAWER_WIDTH = 240;

// Create context for sidebar state
const SidebarContext = createContext();

// Custom hook to use sidebar context
export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
};

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    flexGrow: 1,
    padding: 0,
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: 0,
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: `calc(100% - ${DRAWER_WIDTH}px)`,
      marginLeft: 0,
    },
    ...(open && {
      transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginLeft: 0
    }),
  }),
);

const Layout = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [fullTableMode, setFullTableMode] = useState(isMobile);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Set full view mode by default on mobile
  useEffect(() => {
    if (isMobile) {
      setFullTableMode(true);
      setSidebarCollapsed(true);
    } else {
      setFullTableMode(false);
    }
  }, [isMobile]);

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleMobileDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const showFullTable = () => {
    setFullTableMode(true);
    setSidebarCollapsed(true);
  };

  const hideFullTable = () => {
    setFullTableMode(false);
    setSidebarCollapsed(false);
  };

  // Force override Material-UI CSS with direct CSS injection
  useEffect(() => {
    // Inject CSS to override Material-UI styles
    const styleId = 'sidebar-override-styles';
    let existingStyle = document.getElementById(styleId);
    
    if (existingStyle) {
      existingStyle.remove();
    }
    
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      @media (min-width: 600px) {
        .MuiBox-root[role="main"],
        .css-v4261g {
          margin-left: ${sidebarCollapsed || fullTableMode ? '0px' : '240px'} !important;
          width: ${sidebarCollapsed || fullTableMode ? '100%' : 'calc(100% - 240px)'} !important;
        }
        .MuiAppBar-root {
          margin-left: ${sidebarCollapsed ? '0px' : '240px'} !important;
          width: ${sidebarCollapsed ? '100%' : 'calc(100% - 240px)'} !important;
        }
      }
    `;
    
    document.head.appendChild(style);
    
    return () => {
      if (document.getElementById(styleId)) {
        document.getElementById(styleId).remove();
      }
    };
  }, [sidebarCollapsed, fullTableMode]);

  const sidebarContextValue = {
    sidebarCollapsed,
    fullTableMode,
    mobileOpen,
    toggleSidebar,
    handleMobileDrawerToggle,
    showFullTable,
    hideFullTable,
    setSidebarCollapsed,
    setMobileOpen
  };

  return (
    <SidebarContext.Provider value={sidebarContextValue}>
      <Box sx={{ display: 'flex' }}>
        <CssBaseline />
        {(!fullTableMode || isMobile) && <TopBar />}
        {(!fullTableMode || isMobile) && <Sidebar />}
        <Box
          component="main"
          data-testid="main-content"
          sx={{
            flexGrow: 1,
            p: 0,
            width: sidebarCollapsed || fullTableMode ? '100%' : { sm: `calc(100% - 240px)` },
            marginLeft: sidebarCollapsed || fullTableMode ? '0px !important' : { sm: '240px !important' },
            marginTop: (fullTableMode && !isMobile) ? '0px' : '64px',
            transition: 'all 0.3s ease',
            position: (fullTableMode && !isMobile) ? 'fixed' : 'relative',
            top: (fullTableMode && !isMobile) ? '0px' : 'auto',
            left: (fullTableMode && !isMobile) ? '0px' : 'auto',
            right: (fullTableMode && !isMobile) ? '0px' : 'auto',
            bottom: (fullTableMode && !isMobile) ? '0px' : 'auto',
            zIndex: (fullTableMode && !isMobile) ? 1000 : 'auto',
            backgroundColor: (fullTableMode && !isMobile) ? 'white' : 'transparent',
            overflow: (fullTableMode && !isMobile) ? 'auto' : 'visible',
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </SidebarContext.Provider>
  );
};

export default Layout; 